<template>
    <section v-if="visiblePhotos.length > 0" class="photo-gallery">
        <div class="container">
            <div class="gallery-grid">
                <figure
                    v-for="(photo, index) in visiblePhotos"
                    :key="photo.id || index"
                    class="gallery-item"
                    @click="openLightbox(index)"
                >
                    <img :src="photo.image" :alt="photo.caption || 'Construction photo'" loading="lazy" />
                    <figcaption v-if="photo.caption || photo.photo_date" class="gallery-caption">
                        <span v-if="photo.caption" class="gallery-caption-text">{{ photo.caption }}</span>
                        <span v-if="photo.photo_date" class="gallery-caption-date">{{ formatDate(photo.photo_date) }}</span>
                    </figcaption>
                </figure>
            </div>
        </div>

        <div v-if="lightboxIndex !== null" class="lightbox" @click.self="closeLightbox">
            <button class="lightbox-close" aria-label="Close" @click="closeLightbox">&times;</button>
            <button
                v-if="visiblePhotos.length > 1"
                class="lightbox-nav lightbox-prev"
                aria-label="Previous"
                @click.stop="prev"
            >&#8249;</button>

            <figure class="lightbox-content">
                <img :src="visiblePhotos[lightboxIndex].image" :alt="visiblePhotos[lightboxIndex].caption || 'Construction photo'" />
                <figcaption v-if="visiblePhotos[lightboxIndex].caption || visiblePhotos[lightboxIndex].photo_date" class="lightbox-caption">
                    <span v-if="visiblePhotos[lightboxIndex].caption">{{ visiblePhotos[lightboxIndex].caption }}</span>
                    <span v-if="visiblePhotos[lightboxIndex].photo_date" class="lightbox-date">{{ formatDate(visiblePhotos[lightboxIndex].photo_date) }}</span>
                </figcaption>
            </figure>

            <button
                v-if="visiblePhotos.length > 1"
                class="lightbox-nav lightbox-next"
                aria-label="Next"
                @click.stop="next"
            >&#8250;</button>
        </div>
    </section>
</template>

<script>
export default {
    props: {
        photos: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            lightboxIndex: null,
        };
    },
    computed: {
        // Respect active flag, guard for null images, sort ascending by order.
        visiblePhotos() {
            if (!Array.isArray(this.photos)) return [];
            return this.photos
                .filter(photo => photo && photo.active !== false && photo.image)
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        },
    },
    methods: {
        formatDate(value) {
            if (!value) return '';
            const date = new Date(value);
            if (isNaN(date.getTime())) return value;
            return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
        },
        openLightbox(index) {
            this.lightboxIndex = index;
        },
        closeLightbox() {
            this.lightboxIndex = null;
        },
        prev() {
            if (this.lightboxIndex === null) return;
            this.lightboxIndex = (this.lightboxIndex - 1 + this.visiblePhotos.length) % this.visiblePhotos.length;
        },
        next() {
            if (this.lightboxIndex === null) return;
            this.lightboxIndex = (this.lightboxIndex + 1) % this.visiblePhotos.length;
        },
        handleKeydown(event) {
            if (this.lightboxIndex === null) return;
            if (event.key === 'Escape') this.closeLightbox();
            else if (event.key === 'ArrowLeft') this.prev();
            else if (event.key === 'ArrowRight') this.next();
        },
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeydown);
    },
};
</script>

<style scoped>
.photo-gallery {
    padding: 2rem 0 3rem;
}

.gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.gallery-item {
    margin: 0;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    background-color: #F6F7F8;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.gallery-item img {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
}

.gallery-caption {
    padding: 0.6rem 0.8rem;
    text-align: left;
}

.gallery-caption-text {
    display: block;
    color: #2c3e50;
    font-size: 0.95rem;
}

.gallery-caption-date {
    display: block;
    color: #6E8496;
    font-size: 0.8rem;
    margin-top: 0.15rem;
}

.lightbox {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.lightbox-content {
    margin: 0;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.lightbox-content img {
    max-width: 90vw;
    max-height: 78vh;
    object-fit: contain;
}

.lightbox-caption {
    color: #fff;
    margin-top: 0.75rem;
    text-align: center;
}

.lightbox-date {
    display: block;
    color: #cfd8df;
    font-size: 0.85rem;
    margin-top: 0.2rem;
}

.lightbox-close {
    position: absolute;
    top: 1rem;
    right: 1.5rem;
    font-size: 2.5rem;
    line-height: 1;
    color: #fff;
    background: transparent;
    border: none;
    cursor: pointer;
}

.lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 3rem;
    line-height: 1;
    color: #fff;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0 1rem;
}

.lightbox-prev {
    left: 1rem;
}

.lightbox-next {
    right: 1rem;
}

@media all and (max-width: 600px) {
    .gallery-item img {
        height: 160px;
    }
}
</style>
